import React, { useMemo, useRef, useState } from "react";
import MenuButton from "../../MenuButton/MenuButton";
import UserServices from "../../../UserServices/UserServices";
import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import MealPlanIntakeForm from "./MealPlanIntakeForm";
import {
  emptyMealPlanIntakeForm,
  mealPlanIntakeFormIsDirty,
  normalizeMealPlanIntakeForm,
  sanitizeMealPlanIntakeFormForSave,
  mergeSavedIntakeForm,
  collectIntakeFormImageUploads,
  collectIntakeFormImageRemovals,
} from "./mealPlanIntakeFormData";
import { downloadMealPlanPackagePdf } from "./mealPlanIntakePdf";
import { fetchClientIntakeDefaults } from "./mealPlanClientAutofill";

export default function ViewMealPlansDetail() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const { id } = useParams();
  const intakePrintRef = useRef(null);

  const [intakeForm, setIntakeForm] = useState(emptyMealPlanIntakeForm());
  const [savedIntakeForm, setSavedIntakeForm] = useState(emptyMealPlanIntakeForm());
  const [planMeta, setPlanMeta] = useState({
    clientName: "",
    planId: id,
    branchId: "",
  });

  const hasUnsavedChanges = useMemo(
    () => mealPlanIntakeFormIsDirty(intakeForm, savedIntakeForm),
    [intakeForm, savedIntakeForm]
  );

  const loadIntakeForm = async (planUuid, clientId, clientName) => {
    try {
      const res = await UserServices.getMealPlanIntakeForm(planUuid);
      const payload = res?.data?.data;
      let saved = normalizeMealPlanIntakeForm(payload?.form_data);

      const hasSavedData =
        payload?.form_data &&
        (saved.name ||
          saved.pre_breakfast_options.some((o) => o.text || o.imageUrl) ||
          saved.breakfast_options.some((o) => o.text || o.imageUrl));

      if (!hasSavedData && clientId) {
        saved = await fetchClientIntakeDefaults(clientId, {
          name: clientName || saved.name,
        });
      }

      setIntakeForm(saved);
      setSavedIntakeForm(saved);
    } catch (err) {
      console.log(err);
      let blank = emptyMealPlanIntakeForm();
      if (clientId) {
        blank = await fetchClientIntakeDefaults(clientId, { name: clientName });
      }
      setIntakeForm(blank);
      setSavedIntakeForm(blank);
    }
  };

  const getRecord = async () => {
    try {
      setLoading(true);
      const response = await UserServices.getMealPlan(id);
      const row = response?.data?.data?.[0];
      setPlanMeta({
        clientName: row?.client_name || "",
        planId: row?.plan_id || id,
        branchId: row?.branch_id || "",
      });
      await loadIntakeForm(row?.plan_id || id, row?.id, row?.client_name);
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    getRecord();
  }, []);

  const handleSave = async () => {
    if (!hasUnsavedChanges) {
      toast.info("No changes to save", { position: "top-center" });
      return;
    }

    setSaving(true);
    try {
      const uploads = collectIntakeFormImageUploads(intakeForm);
      const removals = collectIntakeFormImageRemovals(intakeForm, savedIntakeForm);
      const res = await UserServices.saveMealPlanIntakeForm(
        planMeta.planId || id,
        sanitizeMealPlanIntakeFormForSave(intakeForm),
        uploads,
        removals
      );

      if (res?.data?.status) {
        const merged = mergeSavedIntakeForm(res.data.data, intakeForm);
        setIntakeForm(merged);
        setSavedIntakeForm(merged);
        toast.success("Diet plan saved successfully!", {
          position: "top-center",
          autoClose: 2500,
        });
      } else {
        throw new Error("Save failed");
      }
    } catch (err) {
      console.log(err);
      toast.error("Could not save diet plan", { position: "top-center" });
    }
    setSaving(false);
  };

  const handleDownloadPdf = async () => {
    if (hasUnsavedChanges) {
      toast.warning("Please save your changes before downloading PDF", {
        position: "top-center",
      });
      return;
    }

    setPdfLoading(true);
    try {
      const printRoot = intakePrintRef.current?.querySelector(".mpi-print-root");
      await downloadMealPlanPackagePdf({
        intakePrintRoot: printRoot,
        meta: planMeta,
        fetchImageData: UserServices.getMealPlanImageData,
      });
      toast.success("Diet plan PDF downloaded", {
        position: "top-center",
        autoClose: 2000,
      });
    } catch (err) {
      console.log(err);
      toast.error("Could not generate PDF", { position: "top-center" });
    }
    setPdfLoading(false);
  };

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />

          <div className="card">
            <div className="card-header py-3">
              <h5 className="mb-0 font-20 d-flex flex-wrap align-items-center justify-content-between gap-2">
                <span>View / Edit Diet Plan</span>
                <span className="d-flex gap-2 flex-wrap">
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    disabled={!hasUnsavedChanges || saving || loading}
                    onClick={handleSave}
                  >
                    <i className="bi bi-check-lg me-1" />
                    {saving ? "Saving…" : "Save"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    disabled={pdfLoading || loading || saving || hasUnsavedChanges}
                    onClick={handleDownloadPdf}
                  >
                    <i className="bi bi-file-earmark-pdf me-1" />
                    {pdfLoading ? "Generating…" : "Download PDF"}
                  </button>
                  <Link to="/view-meal-plan" className="btn btn-primary btn-sm">
                    Back
                  </Link>
                </span>
              </h5>
              {planMeta.clientName ? (
                <p className="text-muted small mb-0 mt-1">
                  Client: <strong>{planMeta.clientName}</strong>
                  {planMeta.planId ? (
                    <span className="ms-2">Plan ID: {planMeta.planId}</span>
                  ) : null}
                  {hasUnsavedChanges ? (
                    <span className="ms-2 text-warning">Unsaved changes</span>
                  ) : (
                    <span className="ms-2 text-muted">All changes saved</span>
                  )}
                </p>
              ) : null}
            </div>
            <div className="card-body">
              <MealPlanIntakeForm
                form={intakeForm}
                onChange={setIntakeForm}
                printRef={intakePrintRef}
                branchId={planMeta.branchId}
              />
            </div>
          </div>
        </div>
      </main>
      <div className={`loader ${loading || saving ? "in" : ""}`}>
        <div className="spinner-border main-spin"></div>
      </div>
    </div>
  );
}
